import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from '@mui/material'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useTranslation } from 'react-i18next'
import DateFilter from './DateFilter'
import SuggestingField from './SuggestingField'

/**
 * What one column lets you do, in one place: sort by it, filter on it, or hide it.
 *
 * Used by ColumnTable, so the wording lives in `common` and a column's own values name their
 * namespace through filter.translate, e.g. 'parcels:statuses'.
 *
 * The filter bar used to sit above the table with fifteen fields in it, several of which had no
 * column of their own, so reading a row told you nothing about what you could do with it. The
 * column descriptor now carries its own filter, and this renders whichever kind that is.
 */

export const filterControl = ({ column, value, onChange, t }) => {
  const { filter } = column
  if (!filter) return null

  if (filter.kind === 'dateRange') {
    return (
      <DateFilter
        label={column.label}
        value={value}
        onChange={onChange}
        size="small"
        fullWidth
      />
    )
  }

  if (filter.kind === 'multiSelect') {
    const selected = value || []
    return (
      <TextField
        select
        fullWidth
        size="small"
        label={column.label}
        value={selected}
        onChange={(e) =>
          onChange(e.target.value.length ? e.target.value : undefined)
        }
        SelectProps={{
          multiple: true,
          renderValue: (chosen) =>
            chosen
              .map((v) =>
                filter.translate ? t(`${filter.translate}.${v}`) : v
              )
              .join(', '),
        }}
      >
        {(filter.options || []).map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox
              size="small"
              checked={selected.includes(option)}
              sx={{ p: 0.5, mr: 1 }}
            />
            {filter.translate ? t(`${filter.translate}.${option}`) : option}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  if (filter.kind === 'select') {
    return (
      <TextField
        select
        fullWidth
        size="small"
        label={column.label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <MenuItem value="">
          <em>{t('anyValue')}</em>
        </MenuItem>
        {(filter.options || []).map((option) => {
          const key = typeof option === 'string' ? option : option.value
          const label = typeof option === 'string' ? option : option.label
          return (
            <MenuItem key={key} value={key}>
              {filter.translate ? t(`${filter.translate}.${label}`) : label}
            </MenuItem>
          )
        })}
      </TextField>
    )
  }

  // A count, so the question is "at least this many" rather than a word to match.
  if (filter.kind === 'number') {
    return (
      <TextField
        fullWidth
        autoFocus
        size="small"
        type="number"
        label={column.label}
        value={value ?? ''}
        inputProps={{ min: 0 }}
        onChange={(e) => onChange(e.target.value || undefined)}
      />
    )
  }

  // A text filter on a field the server can suggest values for offers them as you type. The
  // others stay a plain box: a suggestion list is worth having where the same values come back
  // again and again, and worth nothing where every one of them is unique.
  if (filter.suggest) {
    return (
      <SuggestingField
        label={column.label}
        field={filter.suggest}
        value={value}
        onChange={onChange}
      />
    )
  }

  return (
    <TextField
      fullWidth
      autoFocus
      size="small"
      label={column.label}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || undefined)}
    />
  )
}

export default function ColumnMenu({
  column,
  anchorEl,
  onClose,
  sorting,
  onSort,
  value,
  onFilter,
  onHide,
}) {
  const { t } = useTranslation('common')
  if (!column) return null

  const sortedBy = sorting.orderBy === (column.sortBy || column.id)
  const sortButton = (order, Icon, label) => (
    <Button
      size="small"
      fullWidth
      startIcon={<Icon fontSize="small" />}
      sx={{ justifyContent: 'flex-start' }}
      variant={sortedBy && sorting.order === order ? 'contained' : 'text'}
      onClick={() =>
        onSort(sortedBy && sorting.order === order ? undefined : order)
      }
    >
      {label}
    </Button>
  )

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Box sx={{ p: 1.5, width: 260 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {column.label}
        </Typography>

        {column.sortable ? (
          <>
            {sortButton('asc', ArrowUpward, t('sortAscending'))}
            {sortButton('desc', ArrowDownward, t('sortDescending'))}
            <Divider sx={{ my: 1 }} />
          </>
        ) : null}

        {column.filter ? (
          <Box sx={{ mb: 1 }}>
            {filterControl({ column, value, onChange: onFilter, t })}
          </Box>
        ) : null}

        <Button
          size="small"
          fullWidth
          startIcon={<VisibilityOff fontSize="small" />}
          sx={{ justifyContent: 'flex-start' }}
          onClick={() => {
            onHide()
            onClose()
          }}
        >
          {t('hideColumn')}
        </Button>
      </Box>
    </Popover>
  )
}
